/**
 * crucible#3501 / hw#2080 — "Report this problem" on the deploy-failure
 * banner.
 *
 * Pure assembly of the {@link GameBugReportInput} the banner's button
 * needs, from the raw deploy-failure signals Workspace.tsx already
 * tracks (`deployErrorCode`, `deployMessage`, `localFailedCheck`, the
 * remote `DeployFailureContext`'s first failed job, and whether the
 * CURRENT banner is a readiness REFUSAL rather than a real failure).
 * Separated from Workspace.tsx (a very large component with no
 * existing render-level test harness) so this assembly logic is
 * independently unit-testable, mirroring `deploy-action-state.ts` and
 * `deploy-failure-copy.ts` in this same directory.
 *
 * A readiness REFUSAL (#3490 — "not built yet", "not on the deploy
 * branch", "already up to date") is actionable GUIDANCE, not a bug:
 * there is nothing to report, so this resolves `null` on that path
 * even when a GitHub remote is configured. The real failure path
 * (a CI run failed, or a local pre-flight check failed) is where the
 * report is meaningful.
 */
import { buildGameBugReportUrl, type GameBugReportResult } from '../game-bug-report';
import type { DeployState } from './deploy-action-state';
import type { WorkspaceHeaderMode } from './workspace-view-types';

export interface DeployBugReportSignals {
  /** True when the CURRENT banner is a readiness refusal (#3490) —
   *  the plain reason already IS the headline, and there's no failure
   *  to report. */
  isReadinessRefusal: boolean;
  deployErrorCode: string | null | undefined;
  deployMessage: string | null | undefined;
  /** The remote failure context's first failed job's step name, if any
   *  (`deployFailureContext.failedJobs[0].failedStep`). */
  failedStepFromContext: string | undefined;
  /** The local pre-flight check name, when no remote context exists
   *  yet (CI never ran). Same fallback WorkspaceHeader's chip uses. */
  localFailedCheck: string | null | undefined;
  repoFullName: string | undefined;
  gameId: string | undefined;
  gameName: string;
  foundryVersion: string | undefined;
  gameVersion: string | undefined;
  branch: string | null | undefined;
  placement: 'local' | 'cloud' | undefined;
  surface: 'desktop' | 'cloud';
  workspaceMode: WorkspaceHeaderMode;
  deployState: DeployState;
}

/**
 * Resolve the deploy-failure banner's bug-report URL, or `null` when
 * there's nothing to report (a readiness refusal) or no GitHub remote
 * is configured (`buildGameBugReportUrl`'s own null case).
 */
export function buildDeployFailureBugReport(
  signals: DeployBugReportSignals,
): GameBugReportResult | null {
  if (signals.isReadinessRefusal) return null;

  const failingStep = signals.failedStepFromContext ?? signals.localFailedCheck ?? undefined;
  const errorDetail = signals.deployMessage
    ? signals.deployErrorCode
      ? `${signals.deployErrorCode}: ${signals.deployMessage}`
      : signals.deployMessage
    : undefined;

  return buildGameBugReportUrl({
    repoFullName: signals.repoFullName ?? '',
    gameId: signals.gameId ?? '',
    gameName: signals.gameName,
    foundryVersion: signals.foundryVersion,
    gameVersion: signals.gameVersion,
    branch: signals.branch ?? undefined,
    placement: signals.placement,
    surface: signals.surface,
    workspaceMode: signals.workspaceMode,
    deployState: signals.deployState,
    failingStep,
    errorDetail,
  });
}
